interface Callback {
	callback: () => void;
	context: object | null;
}

interface CallbacksInterface {
	[key: string]: Callback[];
}

export class EventEmitterStore {
	private _callbacks: CallbacksInterface;

	constructor() {
		this._callbacks = {};
	}

	/**
	 * Get callback's index for the event.
	 *
	 * @param {string} event
	 * @param {callback} callback
	 *
	 * @return {number}
	 */
	private _findCallback(event: string, callback: () => void): number {
		return this._callbacks[event]?.findIndex(ele => ele.callback === callback) ?? -1;
	}

	/**
	 * Add the listener.
	 *
	 * @param {string} event
	 * @param {function} callback
	 * @param {object|null} context - In than context will be called callback.
	 *
	 * @return {this}
	 */
	public on(event: string, callback: () => void, context: object | null = null): this {
		if (typeof callback !== 'function') {
			throw new TypeError(`callback is not a function`);
		}

		this._callbacks[event] = this._callbacks[event] ?? [];

		const callId = this._findCallback(event, callback);

		if (callId === -1) {
			this._callbacks[event].push({ callback, context });
		}

		return this;
	}

	/**
	 * Trigger an event.
	 *
	 * @param {string} event
	 * @param {...args} args _ All arguments which should be passed into callback
	 *
	 * @return {this}
	 */
	public emit(event: string, ...args: unknown[]): this {
		const callbacks = this._callbacks[event];

		callbacks?.forEach(({ callback, context }) => {
			callback.apply(context, args as []);
		});

		return this;
	}

	/**
	 * Remove a selected callback from the event.
	 *
	 * @param {string} event
	 * @param {function} callback
	 *
	 * @return {this}
	 */
	public off(event: string, callback: () => void): this {
		const callbacks = this._callbacks[event];

		if (callbacks && callback) {
			const callId = this._findCallback(event, callback);

			if (callId !== -1) {
				callbacks.splice(callId, 1);
			}
		}

		return this;
	}

	/**
	 * Clear all events and callback links.
	 *
	 * @return {this}
	 */
	public clear(): this {
		this._callbacks = {};

		return this;
	}
}

const eventStore: { [key: string]: EventEmitterStore } = {};

/**
 * Get Event emitter singelton via scope name.
 *
 * @param {string} name - 'global'
 *
 * @return {instanceOf EventEmitter}
 */
export default function EventEmitter(name = 'global'): EventEmitterStore {
	let emitterInstance: EventEmitterStore | null = null;

	if (!eventStore[name]) {
		emitterInstance = new EventEmitterStore();

		eventStore[name] = emitterInstance;
	}

	return eventStore[name];
}
